import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { Spin, Form, Tabs, Breadcrumb, Result } from "antd";
import get from "lodash/get";
import map from "lodash/map";
import head from "lodash/head";
import find from "lodash/find";
import dayjs from "dayjs";
import requireAuth from "../../hocs/requireAuth";
import DayGraphSection from "../../components/graphs/dayGraphSection";
import HeaderSelector from "../../components/graphs/dayGraph/headerSelector";
import { fetchDayReportGraph } from "../../redux/actions/reportActions";
import { BarChartOutlined, CalendarOutlined } from "@ant-design/icons";
import { fetchCompanies } from "../../redux/actions/companyActions";
import { fetchCompanyTemplates } from "../../redux/actions/templateActions";
import { getCurrentDateRange } from "../../utilities/report";
import { COMPANY_ADMIN_ROLE_ACCESS } from "../../constants";

const PageBreadcrumb = () => {
  return (
    <Breadcrumb
      style={{ fontSize: "14px" }}
      items={[
        {
          title: <BarChartOutlined style={{ fontSize: "16px" }} />,
        },
        {
          title: (
            <>
              <CalendarOutlined style={{ fontSize: "16px" }} />
              <span>Day</span>
            </>
          ),
        },
        {
          title: "Graph Report",
        },
      ]}
    />
  );
};

const initialState = {
  template: undefined,
  graph_report: undefined,
  interval: "week",
  date: getCurrentDateRange("week", dayjs()),
};

const DayGraphReportsScreen = () => {
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const [config, setConfig] = useState(null);
  const [showEmpty, setShowEmpty] = useState(false);

  const [selectedTemplate, setSelectedTemplate] = useState(initialState);

  const [filter, setFilters] = useState({
    company: undefined,
    templateType: undefined,
  });
  const [templates, setTemplates] = useState([]);

  const setinitialCompany = (company) => {
    setFilters({
      ...filter,
      templateType: undefined,
      company,
    });
    setConfig({
      ...config,
      companies: [company],
      templates: get(company, "template_access_list"),
    });
    form.setFieldsValue({
      template: undefined,
      Company: get(company, "id"),
    });
  };

  const getCompanies = () => {
    setLoading(true);
    dispatch(fetchCompanies())
      .then((result) => {
        if (result.length === 1) {
          setinitialCompany(result[0]);
        } else {
          setConfig({ companies: result });
        }
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  };

  const getReportGraph = (template, interval, dateArr) => {
    setLoading(true);

    const query = {
      uuid: get(template, "id"),
      from_date: dateArr[0],
      to_date: dateArr[1],
    };

    dispatch(fetchDayReportGraph(query))
      .then((result) => {
        setSelectedTemplate({
          template,
          graph_report: get(result, "reports"),
          meta: get(result, "meta"),
          interval,
          date: dateArr,
        });
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  };

  const getCompanyTemplates = () => {
    setLoading(true);
    dispatch(
      fetchCompanyTemplates(get(filter, "company.id"), {
        type: get(filter, "templateType"),
        excludePaused: true,
      }),
    )
      .then((result) => {
        setTemplates(result);
        if (result.length > 0) {
          setShowEmpty(false);
          getReportGraph(
            head(result),
            get(selectedTemplate, "interval"),
            get(selectedTemplate, "date"),
          );
        } else {
          setSelectedTemplate(initialState);
          setShowEmpty(true);
        }
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    getCompanies();
  }, []);

  return (
    <div className="p-2 h-full flex flex-col">
      <div className="flex flex-row justify-between pb-4">
        <div>
          <PageBreadcrumb />
        </div>
        <div></div>
      </div>
      <div className="h-full rounded-xl overflow-hidden bg-white flex-1 overflow-y-auto scrollbar-hidden pb-5 min-w-full ">
        <Form form={form}>
          <Spin spinning={loading}>
            <div className="w-fit px-8 py-6">
              <HeaderSelector
                config={config}
                filter={filter}
                form={form}
                getCompanyTemplates={getCompanyTemplates}
                setFilters={setFilters}
                setConfig={setConfig}
              />
            </div>
            {templates.length > 0 && (
              <div className="px-8 overflow-y-auto">
                <Tabs
                  tabBarGutter={10}
                  tabPosition={"left"}
                  type="card"
                  destroyOnHidden
                  activeKey={get(selectedTemplate, "template.id")}
                  onTabClick={(key) => {
                    getReportGraph(
                      find(templates, ["id", key]),
                      get(initialState, "interval"),
                      get(initialState, "date"),
                    );
                  }}
                  items={map(templates, (template) => ({
                    key: get(template, "id"),
                    label: get(template, "name"),
                    children: (
                      <DayGraphSection
                        filter={filter}
                        setLoading={setLoading}
                        selectedTemplate={selectedTemplate}
                        onChangeParams={getReportGraph}
                      />
                    ),
                  }))}
                  onChange={() => {}}
                />
              </div>
            )}
            {showEmpty && (
              <div className="px-8 py-8">
                <Result
                  title={`No template exists...!`}
                  subTitle="Please try again with another company or template type."
                  status="404"
                />
              </div>
            )}
          </Spin>
        </Form>
      </div>
    </div>
  );
};

export default requireAuth(DayGraphReportsScreen, COMPANY_ADMIN_ROLE_ACCESS);
